import React from 'react';

import { Delete } from '@depop/web-thrift-ui/Icons/Delete';
import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ExtendedLink } from '@/components/ExtendedLink';
import { Price } from '@/components/Price';
import { SellingItemImage } from '@/components/SellingItemImage';
import {
  addCheckoutProducts,
  formatOrderSummaryPrice,
  getCheckoutType,
} from '@/modules/checkout/helpers';
import {
  CheckoutType,
  StockStatus,
  type CheckoutProduct,
  type OrderSummaryData,
} from '@/modules/checkout/types';
import { useCheckoutProducts } from '@/modules/checkout/useCheckoutProducts';
import { getNearestSizePictureFormat } from '@/modules/pictures/helpers';
import { LegacyProductStatus } from '@/modules/product/types';

function ProductDescription({
  checkoutSummary,
  product,
  isInternationalShippingUnavailable,
  withSoldOutHandling = false,
  withRemoveButton = false,
}: {
  product: CheckoutProduct;
  checkoutSummary: OrderSummaryData;
  isInternationalShippingUnavailable: boolean;
  withSoldOutHandling?: boolean;
  withRemoveButton?: boolean;
}) {
  const checkoutProducts = useCheckoutProducts();
  const t = useTranslations('checkout');
  const commonT = useTranslations('common');
  const line = checkoutSummary.lines.find(
    (line) => line.product_id === product.id
  );

  if (!line) {
    return null;
  }

  const imageSrc = getNearestSizePictureFormat(product.pictures[0], 80)?.url;
  const sizeName = product.size?.name;
  const isDiscounted = Boolean(line?.undiscounted_product_price);
  const isSoldOut =
    withSoldOutHandling && line.stock_status === StockStatus.NotAvailable;
  const href = `/products/${product.slug}`;

  function handleRemoveItem() {
    if (checkoutProducts) {
      addCheckoutProducts({
        type: getCheckoutType() ?? CheckoutType.SingleProduct,
        products: checkoutProducts.filter((prd) => prd.id !== product.id),
      });
    }
  }

  return (
    <div
      className={styles.wrapper}
      key={product.id}
      data-sold={isSoldOut}
      data-sold-out-handling={withSoldOutHandling}
    >
      <div className={styles.imageContainer}>
        <ExtendedLink className={styles.imageLink} href={href}>
          {isSoldOut ? (
            <SellingItemImage
              className={styles.soldImage}
              src={imageSrc}
              altText={product.description || ''}
              status={LegacyProductStatus.MARKED_AS_SOLD}
            />
          ) : (
            <LazyLoadImage alt={product.description || ''} src={imageSrc} />
          )}
        </ExtendedLink>
      </div>

      {isInternationalShippingUnavailable ? (
        <Caption variant="lg-regular" className={styles.errorText}>
          {t('Error.AskSellerTurnOnInternationalShipping')}
        </Caption>
      ) : (
        <>
          <div className={styles.content}>
            <Body variant="sm-regular" className={styles.description}>
              {product.description}
            </Body>
            {sizeName && (
              <Body variant="sm-regular" className={styles.size}>{`${t(
                'OrderSummarySize'
              )} ${sizeName}`}</Body>
            )}
            {withRemoveButton && (
              <ButtonMinimal
                className={styles.removeButton}
                onClick={handleRemoveItem}
              >
                <Delete title={commonT('Remove')} size="md" />
              </ButtonMinimal>
            )}
          </div>
          <Price
            className={clsx(styles.price, {
              [styles.discountedPrice]: isDiscounted,
            })}
            currencyCode={product.price.currencyName}
            price={formatOrderSummaryPrice(
              line.undiscounted_product_price
                ? line.undiscounted_product_price
                : line.product_price || line.original_price
            )}
            discountedPrice={
              isDiscounted
                ? formatOrderSummaryPrice(line.product_price || '')
                : undefined
            }
          />
        </>
      )}
    </div>
  );
}

export { ProductDescription };
